import React from 'react';
import {
  GestureResponderEvent,
  ImageStyle,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';

import { Icon, ICONS } from './Icon';
import { Pressable, PressableProps } from './Pressable';
import { Glass } from './Glass';
import { useIsInGlass } from 'hooks/isInGlass';
import { theme } from 'lib/theme';
import { useIsInHeader } from 'hooks/isInHeader';

export const ICON_BUTTON_HEIGHT = 44;

export type IconButtonProps = {
  onPress?: (event: GestureResponderEvent) => void;
  onPressIn?: (event: GestureResponderEvent) => void;
  onPressOut?: (event: GestureResponderEvent) => void;
  icon: ICONS;
  iconSize?: number;
  style?: StyleProp<ViewStyle>;
  color?: string;
  disabled?: boolean;
  imageStyle?: StyleProp<ImageStyle>;
  size?: number;
  marginHorizontal?: boolean;
  borderRadius?: number;
  backgroundColor?: string;
  hoverColor?: string;
  pressedStyle?: StyleProp<ViewStyle>;
  hoveredStyle?: StyleProp<ViewStyle>;
  pressedColor?: string;
  showBadge?: boolean;
  accessibilityLabel?: PressableProps['accessibilityLabel'];
  accessibilityHint?: PressableProps['accessibilityHint'];
  glass?: boolean;
};

type Theme = {
  iconColor: string;
  iconColorHovered: string;
  backgroundColor: string;
  backgroundColorHovered: string;
  backgroundColorPressed: string;
  backgroundColorGlass: string;
  backgroundColorHoveredGlass: string;
  backgroundColorPressedGlass: string;
};

enum ThemeTypes {
  'regular' = 'regular',
  'header' = 'header',
}

const iconButtonThemes: Record<ThemeTypes, Theme> = {
  [ThemeTypes.regular]: {
    iconColor: theme.colors.blue,
    iconColorHovered: theme.colors.blueDark,
    backgroundColor: 'transparent',
    backgroundColorHovered: theme.colors.blue10,
    backgroundColorPressed: theme.colors.blue20,
    backgroundColorGlass: 'transparent',
    backgroundColorHoveredGlass: 'transparent',
    backgroundColorPressedGlass: 'transparent',
  },
  [ThemeTypes.header]: {
    iconColor: theme.colors.blue,
    iconColorHovered: theme.colors.blueDark,
    backgroundColor: theme.colors.headerButtonBackground,
    backgroundColorHovered: theme.colors.headerButtonBackgroundHover,
    backgroundColorPressed: theme.colors.headerButtonBackgroundPressed,
    backgroundColorGlass: 'transparent',
    backgroundColorHoveredGlass: 'transparent',
    backgroundColorPressedGlass: 'transparent',
  },
};

export const IconButton: React.FC<IconButtonProps> = ({
  color,
  disabled: incomingDisabled,
  icon,
  iconSize,
  imageStyle,
  marginHorizontal = false,
  onPress,
  onPressIn,
  onPressOut,
  backgroundColor,
  hoverColor,
  pressedColor,
  pressedStyle,
  hoveredStyle,
  size,
  style,
  borderRadius,
  showBadge,
  accessibilityLabel,
  accessibilityHint,
  glass,
}) => {
  const isInHeader = useIsInHeader();
  const isInGlass = useIsInGlass();
  const finalSize = size || ICON_BUTTON_HEIGHT;

  const disabled = incomingDisabled || (!onPress && !onPressIn);
  const withGlass = glass !== undefined ? glass : isInGlass ? false : true;

  const iconButtonTheme = isInHeader
    ? iconButtonThemes.header
    : iconButtonThemes.regular;

  const [iconColor, setIconColor] = React.useState(iconButtonTheme.iconColor);
  const handleHoverIn = React.useCallback(() => {
    setIconColor(iconButtonTheme.iconColorHovered);
  }, [iconButtonTheme.iconColorHovered]);
  const handleHoverOut = React.useCallback(() => {
    setIconColor(iconButtonTheme.iconColor);
  }, [iconButtonTheme.iconColor]);

  return (
    <Glass
      glass={withGlass}
      disabled={disabled}
      style={{
        width: finalSize,
        height: finalSize,
        marginRight: marginHorizontal ? theme.buttonMargin * 2 : 0,
        marginLeft: marginHorizontal ? theme.buttonMargin * 2 : 0,
        borderRadius: borderRadius || finalSize,
      }}
      backgroundColor={backgroundColor}
    >
      <Pressable
        disabled={disabled}
        style={[
          {
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.grid,
            width: finalSize,
            height: finalSize,
            borderRadius: borderRadius || finalSize,
            backgroundColor:
              backgroundColor ??
              (isInGlass
                ? iconButtonTheme.backgroundColorGlass
                : iconButtonTheme.backgroundColor),
          },
          disabled && !withGlass
            ? { opacity: theme.opacityDisabled }
            : undefined,
          style,
        ]}
        hoveredStyle={[
          {
            backgroundColor:
              hoverColor ??
              (isInGlass
                ? iconButtonTheme.backgroundColorHoveredGlass
                : iconButtonTheme.backgroundColorHovered),
          },
          hoveredStyle,
        ]}
        pressedStyle={[
          {
            backgroundColor:
              pressedColor ??
              (isInGlass
                ? iconButtonTheme.backgroundColorPressedGlass
                : iconButtonTheme.backgroundColorPressed),
          },
          { opacity: 0.5 },
          pressedStyle,
        ]}
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
      >
        <Icon
          source={icon}
          size={iconSize || 21}
          color={color ?? iconColor}
          style={imageStyle}
        />
        {showBadge && (
          <View
            style={{
              position: 'absolute',
              right: 2,
              bottom: 2,
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: theme.colors.orange,
              zIndex: 1,
              elevation: 1,
            }}
          />
        )}
      </Pressable>
    </Glass>
  );
};
